import {
  RecipeDetailResponse,
  RecipeFavoriteQuery,
  RecipeFavoriteResponse,
  RecipeFavoriteTagResponse,
  RecipeHomepageResponse,
  RecipeListQuery,
  RecipeListResponse,
  RecipeSectionResponse,
  RecipeTagsCountResponse,
  RecipeTagsQuery,
  RecipeTagsResponse,
} from '@diamond/shared/types';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError, isAxiosError } from 'axios';
import { query } from 'urlcat';

export const getRecipeHomepage = async (token: string) => {
  try {
    const url = '/recipe/homepage';
    const { data } = await apiInstance(token).get<RecipeHomepageResponse>(url);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setUser({
          ...user,
        });
        scope.setLevel('error');
      });
    }
  }
  throw new Error('Error Get RecipeHomepage');
};

export const getRecipeDetail = async (
  slug: string,
  token: string,
  warehouse?: string
) => {
  try {
    const response = await apiInstance(token).get(
      `/recipe/${slug}${warehouse && `?warehouse=${warehouse}`}`
    );
    return response.data as RecipeDetailResponse;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('RecipeDetail', `get recipe detail error`);
        scope.setUser({
          ...user,
          additional: {
            warehouse: warehouse,
          },
        });
        scope.setLevel('error');
      });
    }
  }
  throw new Error('Get Recipe Detail');
};
export const postRecipeFavorite = async (
  access_token: string,
  recipeId: string
) => {
  return apiInstance(access_token).post(`/recipe/favorite`, {
    recipe_id: recipeId,
  });
};

export async function getRecipeTags(
  token: string | null,
  params: RecipeTagsQuery
) {
  const { data } = await apiInstance(token).get<RecipeTagsResponse>(
    '/recipe/tag',
    {
      params,
    }
  );
  return data;
}

export async function getRecipeTagsCount(token: string | null) {
  const { data } = await apiInstance(token).get<RecipeTagsCountResponse>(
    '/recipe/count'
  );
  return data;
}

export async function getRecipeSection(token: string | null) {
  const resp = await apiInstance(token).get<RecipeSectionResponse>(
    '/recipe/section'
  );
  return resp;
}

export async function getRecipeList(
  token: string | null,
  params: RecipeListQuery
) {
  // sanitize params to avoid sending empty strings/nulls (e.g., warehouse)
  const cleanedParams = Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== '' && v !== null && v !== undefined
    )
  ) as Record<string, any>;

  const { data } = await apiInstance(token).get<RecipeListResponse>('/recipe', {
    params: cleanedParams as RecipeListQuery,
    paramsSerializer: (params: Record<string, any>) =>
      query(params, { arrayFormat: 'comma' }),
  });
  return {
    data: data.data,
    nextPage: params.page < data.pagination.pages ? params.page + 1 : null,
  };
}

export async function getRecipeFavorite(
  token: string | null,
  params: RecipeFavoriteQuery
) {
  const { data } = await apiInstance(token).get<RecipeFavoriteResponse>(
    '/recipe/favorite',
    {
      params,
      paramsSerializer: (params: Record<string, any>) =>
        query(params, { arrayFormat: 'comma' }),
    }
  );
  return {
    data: data.data,
    nextPage: params.page < data.pagination.pages ? params.page + 1 : null,
  };
}

export async function getRecipeFavoriteTag(token: string | null) {
  const { data } = await apiInstance(token).get<RecipeFavoriteTagResponse>(
    '/recipe/favorite/tag'
  );
  return data;
}
